import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { getErrorMessage } from "../../locales/translationUtils";
import { updatePasswordService, getListaUsuarios, getUsuarioById, 
  deleteUsuario, UsuarioApiResponse,  createUsuario, 
  updateUsuario, UsuarioApiCreateResponse, UsuarioPutVM, UsuarioVM, 
  getUsuariosPaginated,
  getUsuarios} from "../../services/userService";
import { ApiResponse } from "../../services/api";
import Paginacao from "../../types/paginacao";
import User from "../../types/user";
import LoadingOverlay from "../../components/Loading";

interface UserLogin {
  username: string;
  password: string;
  id: number,
  rememberMe: boolean;
}

interface UpdatePasswordData {
  usuarioId: number;
  currentPassword: string;
  newPassword: string;
}

interface UserContextProps {
  apiResponse: ApiResponse<User> | null;
  usuarios: User[] | null;
  usuarioById: UsuarioApiResponse | null;
  loading: boolean;
  error: string | null;
  userLogin: UserLogin | null;
  updatePassword: (data: UpdatePasswordData) => Promise<void>;
  fetchUsuarios: () => Promise<User[]>;
  fetchUsuarioById: (id: number) => Promise<UsuarioApiResponse | null>;
  postUsuario: (cargo: UsuarioVM) => Promise<UsuarioApiCreateResponse | null>;
  putUsuario: (id: number, cargo: UsuarioPutVM) => Promise<UsuarioApiResponse | null>;
  excluiUsuario: (id: number) => Promise<boolean>;
  fetchUsuariosPaginated: (paginacao: Paginacao | null) => void;
}

interface UserProviderProps {
  children: React.ReactNode;
}

const UserContext = createContext<UserContextProps | null>(null);

const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [apiResponse, setApiResponse] = useState<ApiResponse<User> | null>(null);
  const [usuarios, setUsuarios] = useState<User[] | null>(null);
  const [usuarioById, setUsuarioById] = useState<UsuarioApiResponse | null>(null);
  const [userLogin, setUserLogin] = useState<UserLogin | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUsuarios = useCallback(async (): Promise<User[]> => {
    setError(null);
    try {
      setLoading(true);
      const response = await getListaUsuarios();
      setUsuarios(response.values || []);
      return response.values || [];
    } catch (err) {
      setError(getErrorMessage("fetchUsuarioError"));
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsuariosPaginated = useCallback(async (paginacao: Paginacao | null) => {
    setError(null);
    const loadingTimeout = setTimeout(() => {
      // Se demorar mais de 30s, force desativar loading para evitar tela travada
      console.warn('fetchUsuariosPaginated está demorando muito, forçando desativação do loading');
      setLoading(false);
    }, 30000);
    
    try {
      setLoading(true);
      const response = (paginacao) ? await getUsuariosPaginated(paginacao) : await getUsuarios();
      clearTimeout(loadingTimeout);
      setApiResponse(response);
      setUsuarios(response.data.values || []);
    } catch (err: any) {
      clearTimeout(loadingTimeout);
      // Se for erro 401, não seta erro pois o interceptor já vai redirecionar
      if (err?.response?.status !== 401) {
        setError(getErrorMessage("listUsersError"));
      }
      console.error('Erro em fetchUsuariosPaginated:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsuarioById = useCallback(async (id: number) => {
    setError(null);
    try {
      setLoading(true);
      const response = await getUsuarioById(id);
      setUsuarioById(response);
      return response;
    } catch (err) {
      setError(getErrorMessage("fetchUsuarioByIdError"));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const updatePassword = async ({ currentPassword, newPassword, usuarioId }: UpdatePasswordData) => {
    if (!userLogin) {
      throw new Error("Usuário não autenticado.");
    }

    try {
      await updatePasswordService(currentPassword, newPassword, usuarioId);
      setUserLogin((prevUser) => (prevUser ? { ...prevUser, password: newPassword } : null));
    } catch (error) {
      console.error("Erro ao atualizar senha:", error);
      throw error;
    }
  };

  const postUsuario = useCallback(async (cargo: UsuarioVM) => {
      setError(null);
      try {
        setLoading(true);
        const response = await createUsuario(cargo);
        // Não recarrega a lista aqui - deixa o componente fazer após navegação
        return response;
      } catch (err: any) {
        console.error('Erro em postUsuario:', err);
        setError(getErrorMessage("createUserError"));
        throw err; // Re-lança o erro para o componente tratar
      } finally {
        setLoading(false);
      }
    }, []);
  
    const putUsuario = useCallback(async (id: number, cargo: UsuarioPutVM) => {
      setError(null);
      setLoading(true);
      try {
        const response = await updateUsuario(id, cargo);
        return response;
      } catch (err) {
        setError(getErrorMessage("updateUserError"));
        throw err; // Re-lança o erro para o componente tratar
      } finally {
        setLoading(false);
      }
    }, []);

  const excluiUsuario = useCallback(async (id: number) => {
    setError(null);
    try {
      setLoading(true);
      const sucesso = await deleteUsuario(id);
      if (sucesso) {
        fetchUsuarios();
        return true;
      }
     else {
          return false;
      } 
    } catch (err) {
      setError(getErrorMessage("deleteUsuarioError"));
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchUsuarios]);

  useEffect(() => {
    const storedUser = localStorage.getItem("user") || sessionStorage.getItem("user");
    if (storedUser) {
      setUserLogin(JSON.parse(storedUser));
    }
  }, []);

  return (
    <UserContext.Provider value={
      {
        apiResponse,
        usuarios,
        usuarioById,
        loading,
        error,
        userLogin,
        updatePassword,
        fetchUsuarios,
        fetchUsuarioById,
        postUsuario,
        putUsuario,
        excluiUsuario,
        fetchUsuariosPaginated
      }}>
      <LoadingOverlay isLoading={loading} />
      {children}
    </UserContext.Provider>
  );
};

export default UserProvider;

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error(
      getErrorMessage("wrongUseProviderError", {
        auth: useUser?.name,
        providerName: UserProvider?.name,
      })
    );
  }
  return context;
};
